import { INTENSITY_MAX, INTENSITY_MIN, INTENSITY_POSITIONS, isIntensityPosition, type IntensityPosition } from "@shared/types/intensity";
import { Check, Clock } from "lucide-react";
import * as React from "react";

import { Slider } from "@bcc/components/ui/slider";
import { Tooltip, TooltipContent, TooltipTrigger } from "@bcc/components/ui/tooltip";
import { cn } from "@bcc/lib/utils";

import { IntensityConfidenceBadge } from "./intensity-confidence-badge";
import type { IntensityComparisonCoffee } from "./intensity-scale.types";
import { INTENSITY_BLOCK_COLORS, intensityBlockTextClass } from "./intensity-scale.utils";
import {
  intensitySegmentedTrackGradient,
  setIntensityLabelV2TestId,
  setIntensityMarkerV2TestId,
  setIntensityScaleV2TestId,
} from "./intensity-scale-v2.utils";

/** Where a position sits along the track, as a 0-100 left offset. */
const positionToPercent = (position: IntensityPosition): number => ((position - INTENSITY_MIN) / (INTENSITY_MAX - INTENSITY_MIN)) * 100;

/**
 * One stop's gap, as a percentage of the track. A pin's slot capped to this
 * width can never overlap its neighbour's slot, since the two stops are
 * exactly this far apart and each slot is centred on its own stop.
 */
const STOP_GAP_PERCENT = 100 / (INTENSITY_POSITIONS.length - 1);

/**
 * Horizontal anchor for a pin stack, relative to its `left: X%` point. A pin
 * centred on the very first or last stop would spill half its own width past
 * the track's edge — anchoring those two stacks to the inside edge instead
 * (grow right from the first stop, grow left from the last) keeps every pin
 * fully within the track's bounds. Every other stop still centres normally,
 * since it has room on both sides.
 */
const edgeAnchorClass = (position: IntensityPosition): string => {
  if (position === INTENSITY_MIN) return "translate-x-0";
  if (position === INTENSITY_MAX) return "-translate-x-full";
  return "-translate-x-1/2";
};

interface IntensityComparisonMarkersV2Props {
  coffees: readonly IntensityComparisonCoffee[];
  approvedLabel: string;
  provisionalLabel: string;
  formatScore: (position: IntensityPosition) => string;
}

/** Pin height + gap, and the connector line's own height, in rem — used to size the stack. */
const PIN_ROW_REM = 1.5;
const CONNECTOR_REM = 0.5;

/**
 * Same labelled pins as the grid-based scale, but positioned with a percentage
 * offset instead of a grid column — a slider track has no columns to line
 * them up against. Coffees sharing a position stack into one column instead
 * of overlapping, each column keeping a single connector line down to its
 * slider stop.
 */
function IntensityComparisonMarkersV2({
  coffees,
  approvedLabel,
  provisionalLabel,
  formatScore,
}: Readonly<IntensityComparisonMarkersV2Props>) {
  if (coffees.length === 0) return null;

  const indexed = coffees.map((coffee, index) => ({ coffee, index }));
  const groups = INTENSITY_POSITIONS.map((position) => ({
    position,
    entries: indexed.filter((entry) => entry.coffee.position === position),
  })).filter((group) => group.entries.length > 0);

  const maxStackSize = Math.max(1, ...groups.map((group) => group.entries.length));
  const occupiedPositions = new Set(groups.map((group) => group.position));

  return (
    <div
      className="relative"
      style={{ height: `${String(maxStackSize * PIN_ROW_REM + CONNECTOR_REM)}rem` }}
      data-testid={setIntensityScaleV2TestId("markers")}
    >
      {groups.map(({ position, entries }) => {
        // A pin only risks overlapping a neighbour's pin when that neighbour
        // is also occupied — an isolated marker keeps its normal, more
        // readable width regardless of track size.
        const hasOccupiedNeighbour =
          occupiedPositions.has((position - 1) as IntensityPosition) || occupiedPositions.has((position + 1) as IntensityPosition);

        return (
          <div
            key={position}
            className={cn("absolute bottom-0 flex flex-col-reverse items-center gap-1", edgeAnchorClass(position))}
            // `left` is a percentage of the track because this wrapper is
            // absolutely positioned against it (the nearest positioned
            // ancestor). Giving the wrapper an explicit percentage `width`
            // here, for the same reason, resolves against that same track —
            // so the pin inside it can then cap itself to 100% of a
            // genuinely track-relative size instead of a percentage that
            // would otherwise resolve against this wrapper's own
            // shrink-to-fit (content-based, indeterminate) width.
            style={{
              left: `${String(positionToPercent(position))}%`,
              width: hasOccupiedNeighbour ? `${String(STOP_GAP_PERCENT)}%` : undefined,
            }}
          >
            {/* Connector line down to this position's slider stop, shared by
                every pin stacked in this column. */}
            <span aria-hidden className="h-2 w-px bg-border" />
            {entries.map(({ coffee, index }) => {
              const isApproved = coffee.confidence === "approved";

              return (
                <Tooltip key={`${coffee.name}-${coffee.confidence}-${String(index)}`}>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      data-testid={setIntensityMarkerV2TestId(index)}
                      style={hasOccupiedNeighbour ? { maxWidth: "min(6rem, 100%)" } : undefined}
                      className={cn(
                        "inline-flex max-w-24 items-center gap-1 rounded px-1.5 py-0.5 text-[0.625rem] font-medium leading-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-muted-foreground",
                        isApproved ? "bg-green-100 text-green-900" : "bg-blue-100 text-blue-900"
                      )}
                    >
                      <span className="truncate">{coffee.name}</span>
                      {/* role="img" as well as aria-label: some screen readers
                          ignore aria-label on a bare <svg>. */}
                      {isApproved ? (
                        <Check role="img" aria-label={approvedLabel} className="size-3 shrink-0" />
                      ) : (
                        <Clock role="img" aria-label={provisionalLabel} className="size-3 shrink-0" />
                      )}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent testId={`${setIntensityMarkerV2TestId(index)}-tooltip`}>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{coffee.name}</span>
                      <IntensityConfidenceBadge
                        confidence={coffee.confidence}
                        approvedLabel={approvedLabel}
                        provisionalLabel={provisionalLabel}
                      />
                    </div>
                    <div>{formatScore(coffee.position)}</div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

interface IntensityScaleV2Props {
  /** Currently selected position, or null when the roaster has not scored the coffee yet. */
  value?: IntensityPosition | null;
  onChange?: (position: IntensityPosition) => void;
  /** Locked and dimmed, e.g. because the score is PIM-approved. */
  disabled?: boolean;
  /**
   * Display-only: shows `value` (and the comparison markers) but cannot be
   * moved or clicked, without the dimmed "locked" look. Something else drives
   * the value, so there is nothing for the roaster to operate here.
   */
  readOnly?: boolean;
  comparisonCoffees?: readonly IntensityComparisonCoffee[];
  /** Accessible name for the slider. */
  label: string;
  /** Tooltip text on the green/tick marker. */
  approvedLabel?: string;
  /** Tooltip text on the blue/clock marker. */
  provisionalLabel?: string;
  /** Formats the score line in a comparison marker's tooltip, e.g. "6 / 9". */
  formatScore?: (position: IntensityPosition) => string;
  /** Announced by assistive tech in place of the faked numeric value while unset. */
  unsetLabel?: string;
  /** Accessible name for each position's direct-select button, e.g. "Set intensity to 6 of 9". */
  positionLabel?: (position: IntensityPosition) => string;
  className?: string;
  testId?: string;
}

/** Keys Radix's slider treats as a step/jump — see the activate-on-first-interaction comment below. */
const SLIDER_ACTIVATION_KEYS = new Set(["Home", "End", "PageUp", "PageDown", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]);

/**
 * The roast-colour scale used in the product form: a single Radix slider
 * thumb over the nine-position ramp, with labelled comparison markers for
 * the roaster's other coffees.
 */
function IntensityScaleV2({
  value = null,
  onChange,
  disabled = false,
  readOnly = false,
  comparisonCoffees = [],
  label,
  approvedLabel = "Approved score",
  provisionalLabel = "Provisional score",
  formatScore = (position) => `${position} / ${INTENSITY_MAX}`,
  unsetLabel,
  positionLabel = (position) => `${position} / ${INTENSITY_MAX}`,
  className,
  testId,
}: Readonly<IntensityScaleV2Props>) {
  // Radix always renders its thumb at a concrete position, so an unset value
  // is faked as the lightest position and hidden until the roaster's first
  // interaction gives it a real one.
  const isUnset = value === null;

  const handleValueChange = React.useCallback(
    ([next]: number[]) => {
      if (isIntensityPosition(next)) onChange?.(next);
    },
    [onChange]
  );

  // While unset, Radix's controlled value is faked at the lightest position,
  // so an interaction that resolves to that same position (Home, ArrowLeft at
  // the low end, or clicking the first stop) computes no change and never
  // calls onValueChange — the roaster's first pick of position 1 is silently
  // dropped. Committing the faked value on interaction start fixes this: if
  // the interaction actually resolves elsewhere, Radix's own onValueChange
  // fires immediately after (same event, still before this state flushes)
  // and that later value wins.
  const handleActivate = React.useCallback(() => {
    // Radix disables its own sliding logic while `disabled`, but still
    // delivers the raw pointer/key event to this consumer-supplied handler —
    // without this guard, clicking a visibly locked track would still commit
    // position 1 and dirty the form.
    if (isUnset && !disabled && !readOnly) onChange?.(INTENSITY_MIN);
  }, [isUnset, disabled, readOnly, onChange]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (SLIDER_ACTIVATION_KEYS.has(event.key)) handleActivate();
    },
    [handleActivate]
  );

  return (
    <div className={cn("space-y-2", className)} data-testid={testId ?? setIntensityScaleV2TestId("root")}>
      <IntensityComparisonMarkersV2
        coffees={comparisonCoffees}
        approvedLabel={approvedLabel}
        provisionalLabel={provisionalLabel}
        formatScore={formatScore}
      />

      <Slider
        aria-label={label}
        min={INTENSITY_MIN}
        max={INTENSITY_MAX}
        step={1}
        value={[value ?? INTENSITY_MIN]}
        valueText={isUnset ? unsetLabel : undefined}
        onValueChange={handleValueChange}
        onPointerDown={handleActivate}
        onKeyDown={handleKeyDown}
        disabled={disabled || readOnly}
        className={readOnly && !disabled ? "data-[disabled]:opacity-100" : undefined}
        trackStyle={{ backgroundImage: intensitySegmentedTrackGradient() }}
        trackClassName={cn("h-3 bg-transparent", isUnset && "opacity-50")}
        rangeClassName="hidden"
        thumbClassName={cn(isUnset && "opacity-0")}
        testId={setIntensityScaleV2TestId("slider")}
      />

      {/* Evenly spaced with justify-between rather than a 9-column grid, so
          each number lines up under the slider's actual stop instead of the
          center of a grid cell — the two only coincide at the ends. Every
          circle carries its own position colour, matching the track's
          segments; a ring picks out whichever one is currently selected.
          Each is also a real button, not just a slider-stop label, so a
          position is reachable by direct click/tap without dragging or
          stepping the slider through every position in between. */}
      <div className="flex items-center justify-between px-0.5" data-testid={setIntensityScaleV2TestId("labels")}>
        {INTENSITY_POSITIONS.map((position) => {
          const labelClassName = cn(
            "flex size-5 items-center justify-center rounded-full text-[0.6875rem] font-semibold",
            intensityBlockTextClass(position),
            value === position && "ring-2 ring-foreground ring-offset-1"
          );

          if (readOnly) {
            return (
              <span
                key={position}
                aria-current={value === position ? "true" : undefined}
                data-testid={setIntensityLabelV2TestId(position)}
                className={labelClassName}
                style={{ backgroundColor: INTENSITY_BLOCK_COLORS[position] }}
              >
                {position}
              </span>
            );
          }

          return (
            <button
              key={position}
              type="button"
              disabled={disabled}
              onClick={() => onChange?.(position)}
              aria-label={positionLabel(position)}
              aria-pressed={value === position}
              data-testid={setIntensityLabelV2TestId(position)}
              className={cn(
                labelClassName,
                "transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-muted-foreground disabled:pointer-events-none disabled:opacity-50"
              )}
              style={{ backgroundColor: INTENSITY_BLOCK_COLORS[position] }}
            >
              {position}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { IntensityScaleV2 };
